import { client } from '../../../graphql/client'
import {
  CREATE_PRODUCT_MUTATION,
  CREATE_PRODUCT_PACKAGE_MUTATION,
  CREATE_PRODUCT_VARIANT_MUTATION,
  CREATE_VARIANT_MUTATION,
  DELETE_PRODUCT_MUTATION,
  DELETE_PRODUCT_PACKAGE_MUTATION,
  DELETE_PRODUCT_VARIANT_MUTATION,
  DELETE_VARIANT_MUTATION,
  UPDATE_PRODUCT_PACKAGE_MUTATION,
  UPDATE_PRODUCT_VARIANT_MUTATION,
  UPDATE_VARIANT_MUTATION,
} from '../../../graphql/mutation'
import {
  ICompletedContextState,
  IMesh,
  IModel,
  IProductVariant,
} from '../../type/modelType'
import { v4 as uuidv4 } from 'uuid'
import {
  ProductStatus,
  Shop,
  VariantInput,
  VariantOptionInput,
  VariantType,
  ProductVariant,
  ProductPackage,
  ProductPackageInput,
  TextContentInput,
} from '../../type/backendType'
import { GET_PRODUCT_WITH_VARIANTS } from '../../../graphql/query'

export type ICreatedProductPackage = {
  id: string
  products?: ICreatedProductWithVariant[]
}

export type ICreatedProductWithVariant = {
  id: string
  meshId: string
  meshVariantId: string
}

export const getProductPackages = (
  context: ICompletedContextState
): ProductPackage[] => {
  return context.shop.productPackages.edges
    .filter(edge => edge?.node?.no?.startsWith('meshes-product'))
    .map(edge => ({
      ...edge.node,
    }))
}

export const createProductPackage = async (
  model: IModel,
  context: ICompletedContextState
): Promise<ICreatedProductPackage> => {
  const createdResult = await client.mutate({
    mutation: CREATE_PRODUCT_PACKAGE_MUTATION,
    variables: {
      shopId: context.shop.id,
    },
  })

  if (createdResult.errors) {
    throw createdResult.errors
  }

  const productPackageId = createdResult.data.createProductPackage.id

  try {
    const products = await Promise.all(
      model.meshes.map(meshGroup =>
        createProductWithVariant(meshGroup, context.shop)
      )
    )

    // const updatedResult = await client.mutate({
    //   mutation: UPDATE_PRODUCT_PACKAGE_MUTATION,
    //   variables: {
    //     id: createdResult.data.createProductPackage.id,
    //     input: {
    //       no: `meshes-product-${model.id}`,
    //       title: {
    //         id: uuidv4(),
    //         text: [
    //           {
    //             langCode: context.shop.defaultLanguage,
    //             content: model.title,
    //           },
    //         ],
    //       },
    //       productIds: products.map(p => p.id),
    //     },
    //   },
    // })

    const input = {
      no: `meshes-product-${model.id}`,
      title: {
        id: uuidv4(),
        text: [
          {
            langCode: context.shop.defaultLanguage,
            content: model.title,
          },
        ] as [TextContentInput],
      },
      productIds: products.map(p => p.id) as [string],
    }

    const updatedResult = await updateProductPackage(
      createdResult.data.createProductPackage.id,
      input
    )

    if (updatedResult.errors) {
      await Promise.all(products.map(p => deleteProduct(p.id)))
      throw updatedResult.errors
    }

    context.fetchShop()

    return { id: productPackageId, products }
  } catch (e) {
    await deleteProduct(productPackageId)
    throw e
  }
}

export const updateProductPackage = async (
  id: string,
  input: ProductPackageInput
) => {
  return await client.mutate({
    mutation: UPDATE_PRODUCT_PACKAGE_MUTATION,
    variables: {
      id,
      input,
    },
  })
}

export const createProductWithVariant = async (
  meshGroup: IMesh,
  shop: Shop
): Promise<ICreatedProductWithVariant> => {
  const productId = await createProduct(meshGroup, shop)

  try {
    const variantId = await createVariant(
      `product-meshes-${productId}`,
      'Variants',
      VariantType.MESH,
      meshGroup.variants as IProductVariant[],
      shop
    )

    await upsertProductVariant(
      variantId,
      meshGroup.variants as IProductVariant[],
      productId
    )

    return { id: productId, meshId: meshGroup.id, meshVariantId: variantId }
  } catch (e) {
    await deleteProduct(productId)
    throw e
  }
}

export const createProduct = async (
  meshGroup: IMesh,
  shop: Shop
): Promise<string> => {
  const createdResult = await client.mutate({
    mutation: CREATE_PRODUCT_MUTATION,
    variables: {
      shopId: shop.id,
      status: ProductStatus.ACTIVE,
      input: {
        name: meshGroup.meshGroup,
        title: {
          id: uuidv4(),
          text: [
            {
              langCode: shop.defaultLanguage,
              content: meshGroup.title,
            },
          ],
        },
        no: meshGroup.id,
      },
    },
  })

  if (createdResult.errors) {
    throw createdResult.errors
  }

  return createdResult.data.createProduct.id
}

export const deleteProduct = async (id: string) => {
  await client.mutate({
    mutation: DELETE_PRODUCT_MUTATION,
    variables: {
      id,
    },
  })
}

export const upsertVariant = (
  variantInternalNo: string,
  variantTitle: string,
  variantType: VariantType,
  variants: IProductVariant[],
  shop: Shop
) => {
  const shopVariant = shop.variants.edges.find(
    edge => edge.node.no === variantInternalNo
  )?.node
  if (!shopVariant) {
    createVariant(variantInternalNo, variantTitle, variantType, variants, shop)
  } else {
    updateVariantOptions(shopVariant.id, variants, shop)
  }
}

export const createVariant = async (
  variantInternalNo: string,
  variantTitle: string,
  variantType: VariantType,
  variants: IProductVariant[],
  shop: Shop
): Promise<string> => {
  const createdResult = await client.mutate({
    mutation: CREATE_VARIANT_MUTATION,
    variables: {
      shopId: shop.id,
    },
  })

  if (createdResult.errors) {
    throw createdResult.errors
  }

  const variantId = createdResult.data.createVariant.id

  const input: VariantInput = {
    id: uuidv4(),
    title: {
      id: uuidv4(),
      text: [
        {
          langCode: shop.defaultLanguage,
          content: variantTitle,
        },
      ],
    },
    no: variantInternalNo,
    type: variantType,
    options: variants.map((v, key) => ({
      id: v.id,
      sortOrder: key * 10,
      value: v.id,
      title: {
        id: uuidv4(),
        text: [
          {
            langCode: shop.defaultLanguage,
            content: v.title,
          },
        ],
      },
    })) as [VariantOptionInput],
  }

  const updatedResult = await client.mutate({
    mutation: UPDATE_VARIANT_MUTATION,
    variables: {
      id: createdResult.data.createVariant.id,
      input,
    },
  })

  if (updatedResult.errors) {
    await deleteVariant(variantId)
    throw updatedResult.errors
  }

  return variantId
}

export const updateVariantOptions = async (
  variantId: string,
  variants: IProductVariant[],
  shop: Shop
): Promise<string> => {
  const input: VariantInput = {
    options: variants.map((v, key) => ({
      id: v.id,
      sortOrder: key * 10,
      value: v.id,
      title: {
        id: uuidv4(),
        text: [
          {
            langCode: shop.defaultLanguage,
            content: v.title,
          },
        ],
      },
    })) as [VariantOptionInput],
  }

  const updatedResult = await client.mutate({
    mutation: UPDATE_VARIANT_MUTATION,
    variables: {
      id: variantId,
      input,
    },
  })

  if (updatedResult.errors) {
    throw updatedResult.errors
  }

  return variantId
}

export const deleteVariant = async (id: string) => {
  await client.mutate({
    mutation: DELETE_VARIANT_MUTATION,
    variables: {
      id,
    },
  })
}

export const upsertProductVariant = async (
  variantId: string,
  variants: IProductVariant[],
  productId: string
) => {
  const productVariant = await client.query({
    query: GET_PRODUCT_WITH_VARIANTS,
    variables: {
      id: productId,
    },
  })

  if (
    productVariant.data?.product?.variants?.find(
      (v: ProductVariant) => v.id === variantId
    )
  ) {
    const updatedResult = await client.mutate({
      mutation: UPDATE_PRODUCT_VARIANT_MUTATION,
      variables: {
        productId,
        variantId,
        input: {
          options: variants.map(v => v.id),
        },
      },
    })

    if (updatedResult.errors) {
      throw updatedResult.errors
    }
  } else {
    const createdResult = await client.mutate({
      mutation: CREATE_PRODUCT_VARIANT_MUTATION,
      variables: {
        productId,
        variantId,
        input: {
          options: variants.map(v => v.id),
        },
      },
    })

    if (createdResult.errors) {
      throw createdResult.errors
    }
  }
}

export const deleteProductVariant = async (
  variantId: string,
  productId: string
) => {
  const deletedResult = await client.mutate({
    mutation: DELETE_PRODUCT_VARIANT_MUTATION,
    variables: {
      productId,
      variantId,
    },
  })

  if (deletedResult.errors) {
    throw deletedResult.errors
  }
}

export const deleteProductPackage = async (
  productPackageId: string,
  salesChannelId: string
) => {
  const deletedResult = await client.mutate({
    mutation: DELETE_PRODUCT_PACKAGE_MUTATION,
    variables: {
      productPackageId,
      salesChannelId,
    },
  })

  if (deletedResult.errors) {
    throw deletedResult.errors
  }
}
